import { auth } from '../firebase';
import { resolveAssetIds } from '../data/presetAssetMap';
import { deriveBrandAccentPalette, generateCustomTheme } from '../campaign/stylePresets';

/**
 * Single source of truth for AI-generated themes.
 *
 * Called by:
 *  - WizardBrand step (passes the brand context object from /extract-brand)
 *  - Editor "AI Generate" button (passes a freeform prompt)
 *
 * Both go through /generate-full-theme on the backend, which picks colours,
 * fonts, frame/overlay/layout/motion, AND asset IDs (background, character,
 * platform, pipe, obstacle, collectible, snake skin) appropriate for the
 * game type. We map the response into the campaign `options` update shape.
 */

const ENDPOINT = '/generate-full-theme';

export async function generateFullTheme({ prompt, brand, gameType, generate } = {}) {
  if (!gameType) throw new Error('gameType is required');
  const hasPrompt = typeof prompt === 'string' && prompt.trim().length >= 2;
  const hasBrand = brand && (brand.palette || brand.vibe || brand.title || brand.description);
  if (!hasPrompt && !hasBrand) throw new Error('Provide a prompt or brand context');

  const token = await auth.currentUser.getIdToken();
  const body = { gameType };
  if (hasPrompt) body.prompt = prompt.trim();
  if (hasBrand) body.brand = brand;
  if (generate) body.generate = generate;

  const res = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/app${ENDPOINT}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error || `Theme generation failed (${res.status})`);
  }

  const data = await res.json();
  return data?.theme || data;
}

/**
 * Translate a /generate-full-theme response into a flat options-update object
 * the campaign editor can merge into `campaign.options`.
 *
 * Includes:
 *  - customColors (full 7-colour palette derived from the AI's 3 anchors)
 *  - theme object (font / typography descriptors for the start card)
 *  - frame / overlay / pattern / layout / motion
 *  - asset image fields (backgroundImage, playerImage, etc.) via resolveAssetIds
 *  - title / intro (when present)
 *
 * Caller decides whether to merge or replace.
 */
export function applyFullThemeToOptions(theme, gameType) {
  if (!theme) return {};

  const fullPalette = deriveBrandAccentPalette(
    theme.background,
    theme.primaryAction,
    null // text auto-derived
  );
  if (theme.primary) fullPalette.primary = theme.primary;

  const newTheme = generateCustomTheme(fullPalette, theme.font, gameType);
  const assetUpdates = resolveAssetIds(theme, gameType);

  const update = {
    customColors: { ...fullPalette, headingText: '' },
    theme: newTheme,
    themeCustomised: true,
    selectedThemeName: 'AI generated',
    selectedThemeCategory: null,
    advancedColorsManual: false,
    frameStyle: theme.frameStyle,
    cardOverlayEffect: theme.overlay,
    backgroundPattern: theme.pattern,
    cardLayout: theme.layout,
    motionStyle: theme.motionStyle,
    ...assetUpdates,
  };

  if (theme.title) update.title = String(theme.title).slice(0, 40);
  if (theme.intro) update.intro = String(theme.intro).slice(0, 140);

  return update;
}
